import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, EyeIcon, EyeOffIcon, XCircle, XIcon, ListIcon, FileTextIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, CheckIcon, ClockIcon, PaperclipIcon, UploadIcon, DownloadIcon, Trash2Icon, FileIcon, Loader2Icon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { RichTextEditor } from './RichTextEditor';
import { Customer } from './CustomerCard';
import { Task, TaskAttachment } from './TaskCard';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
interface EditTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Task) => void;
  task: Task | null;
  customers: Customer[];
}
export function EditTaskModal({
  isOpen,
  onClose,
  onSave,
  task,
  customers
}: EditTaskModalProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  // Materials state
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [showMaterials, setShowMaterials] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null);
  const [editingMaterialValue, setEditingMaterialValue] = useState('');
  // Work Log state
  const [workLogDate, setWorkLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [workLogDescription, setWorkLogDescription] = useState('');
  const [workLogHours, setWorkLogHours] = useState('');
  const [workLog, setWorkLog] = useState<Array<{
    date: string;
    description: string;
    hours?: number;
  }>>([]);
  const [showWorkLog, setShowWorkLog] = useState(false);
  const [editingWorkLogIndex, setEditingWorkLogIndex] = useState<number | null>(null);
  const [editingWorkLogDate, setEditingWorkLogDate] = useState('');
  const [editingWorkLogDescription, setEditingWorkLogDescription] = useState('');
  const [editingWorkLogHours, setEditingWorkLogHours] = useState('');
  // Attachments state
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // UPDATED: Expand/Collapse state - Back to all expanded
  const [expandedSections, setExpandedSections] = useState({
    pricing: true,
    materials: true,
    workLog: true,
    attachments: true
  });
  // Load charger prices from Firebase
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  // Load prices from Firebase when modal opens
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setChargerPrices(docSnap.data() as any);
        }
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    if (isOpen) {
      loadPrices();
    }
  }, [isOpen]);
  // Auto-fill material price when charger type changes (only for Ladeboks category)
  useEffect(() => {
    if (task?.category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, task?.category, chargerPrices]);
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setCustomerId(task.customerId);
      setDescription(task.description || '');
      setStatus(task.status);
      setChargerType(task.chargerType || 'Zaptec 1');
      setTaskType(task.taskType || 'Tilbudsopgave');
      // Extract date and time from datetime string
      if (task.agreedPlanning) {
        const [date, time] = task.agreedPlanning.split('T');
        setAgreedPlanning(date);
        setAgreedPlanningTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedPlanning('');
        setAgreedPlanningTime('09:00');
      }
      if (task.agreedInstallation) {
        const [date, time] = task.agreedInstallation.split('T');
        setAgreedInstallation(date);
        setAgreedInstallationTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedInstallation('');
        setAgreedInstallationTime('09:00');
      }
      setCarDeliveryDate(task.carDeliveryDate || '');
      setHours(task.hours?.toString() || '');
      setMaterialPrice(task.materialPrice?.toString() || '');
      setHourlyRate(task.hourlyRate?.toString() || '');
      setQuotePrice(task.quotePrice?.toString() || '');
      setInvoiced(task.invoiced ? 'yes' : 'no');
      setSubTasks(task.subTasks || '');
      setMaterials(task.materials || []);
      setMaterialInput('');
      setMaterialPriceInput('');
      setBulkInput('');
      setBulkPrice('');
      setShowMaterials(false);
      setBulkMode(false);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
      setWorkLog(task.workLog || []);
      setWorkLogDate(new Date().toISOString().split('T')[0]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setShowWorkLog(false);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
      setAttachments(task.attachments || []);
    }
  }, [task]);
  // Calculate total hours including work log hours
  const totalHoursWithWorkLog = useMemo(() => {
    const baseHours = hours ? parseFloat(hours) : 0;
    const workLogHoursTotal = workLog.reduce((sum, entry) => sum + (entry.hours || 0), 0);
    return baseHours + workLogHoursTotal;
  }, [hours, workLog]);
  // Updated calculations to use totalHoursWithWorkLog
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = totalHoursWithWorkLog;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      return {
        totalPrice: totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, totalHoursWithWorkLog]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        const newTotalPrice = currentPrice + additionalPrice;
        setMaterialPrice(newTotalPrice.toString());
      }
      setMaterialInput('');
      setMaterialPriceInput('');
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          const additionalPrice = parseFloat(bulkPrice);
          const newTotalPrice = currentPrice + additionalPrice;
          setMaterialPrice(newTotalPrice.toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  // NEW: Edit material
  const handleStartEditMaterial = (index: number) => {
    setEditingMaterialIndex(index);
    setEditingMaterialValue(materials[index]);
  };
  const handleSaveEditMaterial = () => {
    if (editingMaterialIndex !== null && editingMaterialValue.trim()) {
      const updatedMaterials = [...materials];
      updatedMaterials[editingMaterialIndex] = editingMaterialValue.trim();
      setMaterials(updatedMaterials);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
    }
  };
  const handleCancelEditMaterial = () => {
    setEditingMaterialIndex(null);
    setEditingMaterialValue('');
  };
  const handleAddWorkLog = () => {
    if (workLogDescription.trim() && workLogDate) {
      const entry: {
        date: string;
        description: string;
        hours?: number;
      } = {
        date: workLogDate,
        description: workLogDescription.trim()
      };
      if (workLogHours.trim()) {
        entry.hours = parseFloat(workLogHours);
      }
      setWorkLog([...workLog, entry]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setWorkLogDate(new Date().toISOString().split('T')[0]);
    }
  };
  const handleRemoveWorkLog = (index: number) => {
    setWorkLog(workLog.filter((_, i) => i !== index));
  };
  // NEW: Edit work log
  const handleStartEditWorkLog = (index: number) => {
    const entry = workLog[index];
    setEditingWorkLogIndex(index);
    setEditingWorkLogDate(entry.date);
    setEditingWorkLogDescription(entry.description);
    setEditingWorkLogHours(entry.hours?.toString() || '');
  };
  const handleSaveEditWorkLog = () => {
    if (editingWorkLogIndex !== null && editingWorkLogDescription.trim() && editingWorkLogDate) {
      const updatedWorkLog = [...workLog];
      const entry: {
        date: string;
        description: string;
        hours?: number;
      } = {
        date: editingWorkLogDate,
        description: editingWorkLogDescription.trim()
      };
      if (editingWorkLogHours.trim()) {
        entry.hours = parseFloat(editingWorkLogHours);
      }
      updatedWorkLog[editingWorkLogIndex] = entry;
      setWorkLog(updatedWorkLog);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
    }
  };
  const handleCancelEditWorkLog = () => {
    setEditingWorkLogIndex(null);
    setEditingWorkLogDate('');
    setEditingWorkLogDescription('');
    setEditingWorkLogHours('');
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !task) return;
    try {
      setIsUploading(true);
      const storagePath = `tasks/${task.id}/bilag/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newAttachment: TaskAttachment = {
        name: file.name,
        url,
        storagePath,
        uploadedAt: new Date().toISOString(),
        size: file.size
      };
      setAttachments(prev => [...prev, newAttachment]);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Der opstod en fejl under upload af filen.');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };
  const handleDeleteAttachment = async (attachment: TaskAttachment, index: number) => {
    if (!confirm('Er du sikker på, at du vil slette dette bilag?')) return;
    try {
      const storageRef = ref(storage, attachment.storagePath);
      await deleteObject(storageRef);
      setAttachments(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Der opstod en fejl under sletning af filen.');
    }
  };
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  // NEW: Toggle all sections
  const toggleAllSections = () => {
    const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
    setExpandedSections({
      pricing: !allExpanded,
      materials: !allExpanded,
      workLog: !allExpanded,
      attachments: !allExpanded
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !title.trim() || !customerId) return;
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    // Combine date and time for planning
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    // Combine date and time for installation
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onSave({
      ...task,
      title,
      customerId,
      customerName: customer.name,
      description,
      status,
      chargerType: task.category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined,
      workLog: workLog.length > 0 ? workLog : undefined,
      attachments: attachments.length > 0 ? attachments : undefined
    });
    onClose();
  };
  if (!task) return null;
  const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
  return <AnimatePresence data-id="element-629">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-630" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-631">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-6 my-8" data-id="element-632">
              <div className="flex items-center justify-between mb-6" data-id="element-633">
                <div data-id="element-634">
                  <h2 className="text-2xl font-semibold text-stone-900" data-id="element-635">
                    Rediger opgave
                  </h2>
                  {task?.taskId && <p className="text-sm text-stone-500 mt-1" data-id="element-636">
                      Opgave-ID:{' '}
                      <span className="font-medium text-teal-600" data-id="element-637">
                        {task.taskId}
                      </span>
                    </p>}
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-638">
                  <XIcon className="w-6 h-6" data-id="element-639" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-640">
                {/* Basic Information */}
                <div data-id="element-641">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-642">
                    Grundlæggende information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-643">
                    <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required data-id="element-644" />

                    <Select label="Kunde" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
                  value: '',
                  label: 'Vælg en kunde'
                }, ...customers.map(c => ({
                  value: c.id,
                  label: `${c.name} (${c.customerId})`
                }))]} required data-id="element-645" />
                  </div>

                  <div className="mt-4" data-id="element-646">
                    <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={3} data-id="element-647" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-648">
                    <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
                  value: 'planned',
                  label: 'Planlagt'
                }, {
                  value: 'pending',
                  label: 'Afventende'
                }, {
                  value: 'in-progress',
                  label: 'I gang'
                }, {
                  value: 'done',
                  label: 'Afsluttet'
                }]} data-id="element-649" />

                    <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} data-id="element-650" />
                  </div>
                </div>

                {/* Dates */}
                <div data-id="element-651">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-652">
                    Datoer
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-653">
                    <div data-id="element-654">
                      <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} data-id="element-655" />
                      {agreedPlanning && <div className="mt-2" data-id="element-656">
                          <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} data-id="element-657" />
                        </div>}
                    </div>

                    <div data-id="element-658">
                      <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} data-id="element-659" />
                      {agreedInstallation && <div className="mt-2" data-id="element-660">
                          <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} data-id="element-661" />
                        </div>}
                    </div>
                  </div>

                  {task.category === 'Ladeboks' && <div className="mt-4" data-id="element-662">
                      <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} data-id="element-663" />
                      <p className="text-xs text-stone-500 mt-1" data-id="element-664">
                        Angiv hvornår bilen leveres til kunden
                      </p>
                    </div>}
                </div>

                {/* NEW: Expand/Collapse All Button */}
                <div className="flex justify-end" data-id="element-665">
                  <button type="button" onClick={toggleAllSections} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors" data-id="element-666">
                    {allExpanded ? <>
                        <ChevronUpIcon className="w-4 h-4" data-id="element-667" />
                        Skjul alle sektioner
                      </> : <>
                        <ChevronDownIcon className="w-4 h-4" data-id="element-668" />
                        Udvid alle sektioner
                      </>}
                  </button>
                </div>

                {/* PRICING SECTION - Collapsible */}
                <AnimatePresence data-id="element-669">
                  {expandedSections.pricing ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-670">
                      <div data-id="element-671">
                        <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    pricing: false
                  })} className="flex items-center justify-between w-full mb-4 group" data-id="element-672">
                          <h3 className="text-lg font-semibold text-stone-900" data-id="element-673">
                            Priser og timer
                          </h3>
                          <div className="flex items-center gap-2" data-id="element-674">
                            {totalHoursWithWorkLog > 0 && <span className="text-sm text-stone-500" data-id="element-675">
                                Total: {totalHoursWithWorkLog.toFixed(1)}t
                              </span>}
                            <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-676" />
                          </div>
                        </button>

                        {/* Pricing content */}
                        <div className="space-y-4" data-id="element-677">
                          {task.category === 'Ladeboks' && <Select label="Ladeboks type" value={chargerType} onChange={e => setChargerType(e.target.value as 'Zaptec 1' | 'Zaptec 2' | 'Andet')} options={[{
                      value: 'Zaptec 1',
                      label: 'Zaptec 1'
                    }, {
                      value: 'Zaptec 2',
                      label: 'Zaptec 2'
                    }, {
                      value: 'Andet',
                      label: 'Andet'
                    }]} required data-id="element-678" />}

                          <Select label="Opgavetype" value={taskType} onChange={e => setTaskType(e.target.value as 'Tilbudsopgave' | 'Regningsopgave')} options={[{
                      value: 'Tilbudsopgave',
                      label: 'Tilbudsopgave'
                    }, {
                      value: 'Regningsopgave',
                      label: 'Regningsopgave'
                    }]} required data-id="element-679" />

                          {taskType === 'Tilbudsopgave' ? <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-680">
                                <Input label="Tilbudspris (kr)" type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required data-id="element-681" />
                                <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={task.category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-682" />
                              </div>

                              {task.category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-683">
                                    Materialer pris udfyldes automatisk baseret
                                    på ladeboks type
                                  </p>}

                              <div className="mt-4" data-id="element-684">
                                <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" data-id="element-685" />
                              </div>

                              <div className="mt-4 space-y-3" data-id="element-686">
                                {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-687">
                                    <div className="flex items-center justify-between" data-id="element-688">
                                      <span className="text-sm font-medium text-teal-900" data-id="element-689">
                                        Fortjeneste
                                      </span>
                                      <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-690">
                                        {calculations.profit.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                  </div>}

                                {calculations.realHourlyRate !== null && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-691">
                                    <div className="flex items-center justify-between" data-id="element-692">
                                      <span className="text-sm font-medium text-blue-900" data-id="element-693">
                                        Reel timepris
                                      </span>
                                      <span className="text-lg font-bold text-blue-700" data-id="element-694">
                                        {calculations.realHourlyRate.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr/time
                                      </span>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-1" data-id="element-695">
                                      Fortjeneste / Timer brugt
                                    </p>
                                  </div>}
                              </div>
                            </> : <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-696">
                                <Input label="Timepris (kr)" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required data-id="element-697" />
                                <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required data-id="element-698" />
                                <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={task.category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-699" />
                              </div>

                              {task.category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-700">
                                    Materialer pris udfyldes automatisk baseret
                                    på ladeboks type
                                  </p>}

                              <div className="mt-4 space-y-3" data-id="element-701">
                                <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg" data-id="element-702">
                                  <div className="flex items-center justify-between" data-id="element-703">
                                    <span className="text-sm font-medium text-stone-700" data-id="element-704">
                                      Samlet pris
                                    </span>
                                    <span className="text-lg font-bold text-stone-900" data-id="element-705">
                                      {calculations.totalPrice.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                      kr
                                    </span>
                                  </div>
                                </div>

                                {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-706">
                                    <div className="flex items-center justify-between" data-id="element-707">
                                      <span className="text-sm font-medium text-teal-900" data-id="element-708">
                                        Fortjeneste
                                      </span>
                                      <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-709">
                                        {calculations.profit.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                  </div>}

                                {calculations.realHourlyRate !== null && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-710">
                                    <div className="flex items-center justify-between" data-id="element-711">
                                      <span className="text-sm font-medium text-blue-900" data-id="element-712">
                                        Reel timepris
                                      </span>
                                      <span className="text-lg font-bold text-blue-700" data-id="element-713">
                                        {calculations.realHourlyRate.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr/time
                                      </span>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-1" data-id="element-714">
                                      Fortjeneste / Timer brugt
                                    </p>
                                  </div>}
                              </div>
                            </>}
                        </div>
                      </div>
                    </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-715">
                      <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  pricing: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-716">
                        <ChevronDownIcon className="w-4 h-4" data-id="element-717" />
                        Vis Priser og timer
                        {totalHoursWithWorkLog > 0 && <span className="text-stone-500" data-id="element-718">
                            (Total: {totalHoursWithWorkLog.toFixed(1)}t)
                          </span>}
                      </button>
                    </motion.div>}
                </AnimatePresence>

                {/* Materials Section - Entire section can be collapsed */}
                <AnimatePresence data-id="element-719">
                  {expandedSections.materials ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-720">
                      <div data-id="element-721">
                        <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    materials: false
                  })} className="flex items-center justify-between w-full mb-4 group" data-id="element-722">
                          <h3 className="text-lg font-semibold text-stone-900" data-id="element-723">
                            Materialer
                          </h3>
                          <div className="flex items-center gap-2" data-id="element-724">
                            {materials.length > 0 && <span className="text-sm text-stone-500" data-id="element-725">
                                {materials.length} materialer
                              </span>}
                            <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-726" />
                          </div>
                        </button>

                        {/* Materials content */}
                        <div className="space-y-3" data-id="element-727">
                          <div className="flex items-center justify-between mb-4" data-id="element-728">
                            <div className="flex-1" data-id="element-729" />
                            <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-730">
                              <ListIcon className="w-4 h-4" data-id="element-731" />
                              {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
                            </button>
                          </div>

                          <AnimatePresence mode="wait" data-id="element-732">
                            {bulkMode ? <motion.div key="bulk" initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} transition={{
                        duration: 0.2
                      }} className="space-y-3" data-id="element-733">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-734">
                                  <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-735">
                                    📦 Tilføj stor ordre
                                  </p>
                                  <p className="text-xs text-blue-700" data-id="element-736">
                                    Skriv ét materiale per linje. Prisen lægges
                                    automatisk til materialer pris.
                                  </p>
                                </div>

                                <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m&#10;Stikkontakt&#10;Sikring 16A&#10;Kabelkanal 2m&#10;..." rows={6} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-737" />

                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-738">
                                  <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-739">
                                    💰 Pris for denne ordre (kr)
                                  </label>
                                  <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-2.5 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-740" />
                                  {bulkPrice && materialPrice && <div className="mt-3 pt-3 border-t border-amber-200" data-id="element-741">
                                      <div className="flex items-center justify-between text-sm" data-id="element-742">
                                        <span className="text-amber-700" data-id="element-743">
                                          Nuværende materialer pris:
                                        </span>
                                        <span className="font-medium text-amber-900" data-id="element-744">
                                          {parseFloat(materialPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-sm mt-1" data-id="element-745">
                                        <span className="text-amber-700" data-id="element-746">
                                          Ordre pris:
                                        </span>
                                        <span className="font-medium text-amber-900" data-id="element-747">
                                          +
                                          {parseFloat(bulkPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-base mt-2 pt-2 border-t border-amber-300" data-id="element-748">
                                        <span className="font-semibold text-amber-900" data-id="element-749">
                                          Ny total:
                                        </span>
                                        <span className="font-bold text-amber-900" data-id="element-750">
                                          {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                    </div>}
                                </div>

                                <div className="flex gap-2" data-id="element-751">
                                  <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2" disabled={!bulkInput.trim()} data-id="element-752">
                                    <PlusIcon className="w-4 h-4" data-id="element-753" />
                                    Tilføj alle materialer
                                    {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                                  </Button>
                                  <Button type="button" onClick={() => {
                            setBulkInput('');
                            setBulkPrice('');
                            setBulkMode(false);
                          }} variant="secondary" data-id="element-754">
                                    Annuller
                                  </Button>
                                </div>
                              </motion.div> : <motion.div key="single" initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -10
                      }} transition={{
                        duration: 0.2
                      }} className="space-y-3" data-id="element-755">
                                <div className="flex gap-2" data-id="element-756">
                                  <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddMaterial();
                            }
                          }} placeholder="Skriv materiale navn..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-757" />
                                  <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddMaterial();
                            }
                          }} placeholder="Pris (kr)" className="w-32 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-758" />
                                  <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center gap-2" disabled={!materialInput.trim()} data-id="element-759">
                                    <PlusIcon className="w-4 h-4" data-id="element-760" />
                                    Tilføj
                                  </Button>
                                </div>

                                {materialPriceInput && materialPrice && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-green-50 border border-green-200 rounded-lg p-3" data-id="element-761">
                                    <div className="flex items-center justify-between text-sm" data-id="element-762">
                                      <span className="text-green-700" data-id="element-763">
                                        Nuværende total:
                                      </span>
                                      <span className="font-medium text-green-900" data-id="element-764">
                                        {parseFloat(materialPrice).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1" data-id="element-765">
                                      <span className="text-green-700" data-id="element-766">
                                        Tilføjes:
                                      </span>
                                      <span className="font-medium text-green-900" data-id="element-767">
                                        +
                                        {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-green-300" data-id="element-768">
                                      <span className="font-semibold text-green-900" data-id="element-769">
                                        Ny total:
                                      </span>
                                      <span className="font-bold text-green-900" data-id="element-770">
                                        {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                  </motion.div>}
                              </motion.div>}
                          </AnimatePresence>

                          {/* Materials List with Edit */}
                          {materials.length > 0 && <div className="space-y-2 mt-4" data-id="element-771">
                              <Button type="button" onClick={() => setShowMaterials(!showMaterials)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-772">
                                {showMaterials ? <>
                                    <EyeOffIcon className="w-4 h-4" data-id="element-773" />
                                    Skjul materialer ({materials.length})
                                  </> : <>
                                    <EyeIcon className="w-4 h-4" data-id="element-774" />
                                    Se materialer ({materials.length})
                                  </>}
                              </Button>

                              <AnimatePresence data-id="element-775">
                                {showMaterials && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-776">
                                    <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-777">
                                      {materials.map((material, index) => <motion.li key={index} initial={{
                              opacity: 0,
                              x: -10
                            }} animate={{
                              opacity: 1,
                              x: 0
                            }} exit={{
                              opacity: 0,
                              x: -10
                            }} transition={{
                              delay: index * 0.02
                            }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-778">
                                          {editingMaterialIndex === index ? <div className="flex items-center gap-2" data-id="element-779">
                                              <input type="text" value={editingMaterialValue} onChange={e => setEditingMaterialValue(e.target.value)} onKeyPress={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveEditMaterial();
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditMaterial();
                                  }
                                }} className="flex-1 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" autoFocus data-id="element-780" />
                                              <button type="button" onClick={handleSaveEditMaterial} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded transition-colors" data-id="element-781">
                                                <CheckIcon className="w-4 h-4" data-id="element-782" />
                                              </button>
                                              <button type="button" onClick={handleCancelEditMaterial} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded transition-colors" data-id="element-783">
                                                <XCircle className="w-4 h-4" data-id="element-784" />
                                              </button>
                                            </div> : <div className="flex items-center justify-between" data-id="element-785">
                                              <span className="text-sm text-stone-900" data-id="element-786">
                                                {material}
                                              </span>
                                              <div className="flex items-center gap-1" data-id="element-787">
                                                <button type="button" onClick={() => handleStartEditMaterial(index)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger materiale" data-id="element-788">
                                                  <EditIcon className="w-4 h-4" data-id="element-789" />
                                                </button>
                                                <button type="button" onClick={() => handleRemoveMaterial(index)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern materiale" data-id="element-790">
                                                  <XCircle className="w-4 h-4" data-id="element-791" />
                                                </button>
                                              </div>
                                            </div>}
                                        </motion.li>)}
                                    </ul>
                                  </motion.div>}
                              </AnimatePresence>
                            </div>}

                          {materials.length === 0 && !bulkMode && <p className="text-sm text-stone-500 text-center py-4 bg-stone-50 rounded-lg border border-stone-200 mt-3" data-id="element-792">
                              Ingen materialer tilføjet endnu
                            </p>}
                        </div>
                      </div>
                    </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-793">
                      <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  materials: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-794">
                        <ChevronDownIcon className="w-4 h-4" data-id="element-795" />
                        Vis Materialer
                        {materials.length > 0 && <span className="text-stone-500" data-id="element-796">
                            ({materials.length} materialer)
                          </span>}
                      </button>
                    </motion.div>}
                </AnimatePresence>

                {/* UPDATED: Work Log Section - Entire section can be collapsed */}
                <AnimatePresence data-id="element-797">
                  {expandedSections.workLog ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-798">
                      <div data-id="element-799">
                        <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    workLog: false
                  })} className="flex items-center justify-between w-full mb-4 group" data-id="element-800">
                          <h3 className="text-lg font-semibold text-stone-900" data-id="element-801">
                            Arbejdslog
                          </h3>
                          <div className="flex items-center gap-2" data-id="element-802">
                            {workLog.length > 0 && <span className="text-sm text-stone-500" data-id="element-803">
                                {workLog.length} entries
                              </span>}
                            <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-804" />
                          </div>
                        </button>

                        {/* Work Log content */}
                        <div className="space-y-3" data-id="element-805">
                          <div className="flex gap-2" data-id="element-806">
                            <input type="date" value={workLogDate} onChange={e => setWorkLogDate(e.target.value)} className="w-40 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-807" />
                            <input type="text" value={workLogDescription} onChange={e => setWorkLogDescription(e.target.value)} onKeyPress={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddWorkLog();
                        }
                      }} placeholder="Beskriv hvad der blev lavet..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-808" />
                            <input type="number" step="0.5" value={workLogHours} onChange={e => setWorkLogHours(e.target.value)} onKeyPress={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddWorkLog();
                        }
                      }} placeholder="Timer" className="w-24 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-809" />
                            <Button type="button" onClick={handleAddWorkLog} variant="primary" className="flex items-center gap-2" disabled={!workLogDescription.trim()} data-id="element-810">
                              <PlusIcon className="w-4 h-4" data-id="element-811" />
                              Tilføj
                            </Button>
                          </div>

                          {/* UPDATED: Work Log List with Edit */}
                          {workLog.length > 0 && <div className="space-y-2 mt-4" data-id="element-812">
                              <Button type="button" onClick={() => setShowWorkLog(!showWorkLog)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-813">
                                {showWorkLog ? <>
                                    <EyeOffIcon className="w-4 h-4" data-id="element-814" />
                                    Skjul arbejdslog ({workLog.length})
                                  </> : <>
                                    <FileTextIcon className="w-4 h-4" data-id="element-815" />
                                    Se arbejdslog ({workLog.length})
                                  </>}
                              </Button>

                              <AnimatePresence data-id="element-816">
                                {showWorkLog && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-817">
                                    <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-818">
                                      {workLog.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry, index) => {
                              const originalIndex = workLog.findIndex(e => e === entry);
                              return <motion.li key={originalIndex} initial={{
                                opacity: 0,
                                x: -10
                              }} animate={{
                                opacity: 1,
                                x: 0
                              }} exit={{
                                opacity: 0,
                                x: -10
                              }} transition={{
                                delay: index * 0.02
                              }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-819">
                                              {editingWorkLogIndex === originalIndex ? <div className="space-y-2" data-id="element-820">
                                                  <div className="flex gap-2" data-id="element-821">
                                                    <input type="date" value={editingWorkLogDate} onChange={e => setEditingWorkLogDate(e.target.value)} className="w-40 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-822" />
                                                    <input type="number" step="0.5" value={editingWorkLogHours} onChange={e => setEditingWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-823" />
                                                  </div>
                                                  <input type="text" value={editingWorkLogDescription} onChange={e => setEditingWorkLogDescription(e.target.value)} onKeyPress={e => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveEditWorkLog();
                                    } else if (e.key === 'Escape') {
                                      handleCancelEditWorkLog();
                                    }
                                  }} className="w-full px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" autoFocus data-id="element-824" />
                                                  <div className="flex gap-2" data-id="element-825">
                                                    <button type="button" onClick={handleSaveEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded transition-colors" data-id="element-826">
                                                      Gem
                                                    </button>
                                                    <button type="button" onClick={handleCancelEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-stone-600 bg-stone-100 hover:bg-stone-200 rounded transition-colors" data-id="element-827">
                                                      Annuller
                                                    </button>
                                                  </div>
                                                </div> : <div className="flex items-start justify-between gap-3" data-id="element-828">
                                                  <div className="flex-1" data-id="element-829">
                                                    <div className="flex items-center gap-2 mb-1" data-id="element-830">
                                                      <CalendarIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-831" />
                                                      <span className="text-xs font-medium text-teal-700" data-id="element-832">
                                                        {new Date(entry.date).toLocaleDateString('da-DK', {
                                          weekday: 'short',
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                                      </span>
                                                      {entry.hours && <>
                                                          <span className="text-xs text-stone-400" data-id="element-833">
                                                            •
                                                          </span>
                                                          <ClockIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-834" />
                                                          <span className="text-xs font-medium text-blue-700" data-id="element-835">
                                                            {entry.hours}t
                                                          </span>
                                                        </>}
                                                    </div>
                                                    <p className="text-sm text-stone-900" data-id="element-836">
                                                      {entry.description}
                                                    </p>
                                                  </div>
                                                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-837">
                                                    <button type="button" onClick={() => handleStartEditWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger log" data-id="element-838">
                                                      <EditIcon className="w-4 h-4" data-id="element-839" />
                                                    </button>
                                                    <button type="button" onClick={() => handleRemoveWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern log" data-id="element-840">
                                                      <XCircle className="w-4 h-4" data-id="element-841" />
                                                    </button>
                                                  </div>
                                                </div>}
                                            </motion.li>;
                            })}
                                    </ul>
                                  </motion.div>}
                              </AnimatePresence>
                            </div>}
                        </div>
                      </div>
                    </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-842">
                      <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  workLog: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-843">
                        <ChevronDownIcon className="w-4 h-4" data-id="element-844" />
                        Vis Arbejdslog
                        {workLog.length > 0 && <span className="text-stone-500" data-id="element-845">
                            ({workLog.length} entries)
                          </span>}
                      </button>
                    </motion.div>}
                </AnimatePresence>

                {/* ATTACHMENTS SECTION */}
                <AnimatePresence data-id="element-846">
                  {expandedSections.attachments ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-847">
                      <div data-id="element-848">
                        <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    attachments: false
                  })} className="flex items-center justify-between w-full mb-4 group" data-id="element-849">
                          <h3 className="text-lg font-semibold text-stone-900" data-id="element-850">
                            Bilag
                          </h3>
                          <div className="flex items-center gap-2" data-id="element-851">
                            {attachments.length > 0 && <span className="text-sm text-stone-500" data-id="element-852">
                                {attachments.length} bilag
                              </span>}
                            <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-853" />
                          </div>
                        </button>

                        <div className="space-y-3" data-id="element-854">
                          <div className="flex justify-end mb-4" data-id="element-855">
                            <input type="file" id="file-upload-modal" className="hidden" onChange={handleFileUpload} disabled={isUploading} data-id="element-856" />
                            <label htmlFor="file-upload-modal" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-857">
                              {isUploading ? <>
                                  <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-858" />
                                  Uploader...
                                </> : <>
                                  <UploadIcon className="w-4 h-4" data-id="element-859" />
                                  Tilføj bilag
                                </>}
                            </label>
                          </div>

                          {attachments.length > 0 ? <ul className="space-y-2" data-id="element-860">
                              {attachments.map((attachment, index) => <motion.li key={attachment.storagePath} initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} className="flex items-center justify-between bg-white p-3 rounded-lg border border-stone-200" data-id="element-861">
                                  <div className="flex items-center gap-3 overflow-hidden" data-id="element-862">
                                    <div className="p-2 bg-stone-100 rounded-lg flex-shrink-0" data-id="element-863">
                                      <FileIcon className="w-5 h-5 text-stone-600" data-id="element-864" />
                                    </div>
                                    <div className="min-w-0" data-id="element-865">
                                      <p className="text-sm font-medium text-stone-900 truncate" data-id="element-866">
                                        {attachment.name}
                                      </p>
                                      <div className="flex items-center gap-2 text-xs text-stone-500" data-id="element-867">
                                        <span data-id="element-868">
                                          {new Date(attachment.uploadedAt).toLocaleDateString('da-DK', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                                        </span>
                                        {attachment.size && <>
                                            <span data-id="element-869">•</span>
                                            <span data-id="element-870">
                                              {formatFileSize(attachment.size)}
                                            </span>
                                          </>}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 flex-shrink-0 ml-4" data-id="element-871">
                                    <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="p-2 text-stone-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Download" data-id="element-872">
                                      <DownloadIcon className="w-4 h-4" data-id="element-873" />
                                    </a>
                                    <button type="button" onClick={() => handleDeleteAttachment(attachment, index)} className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Slet" data-id="element-874">
                                      <Trash2Icon className="w-4 h-4" data-id="element-875" />
                                    </button>
                                  </div>
                                </motion.li>)}
                            </ul> : <div className="text-center py-8 bg-stone-50 rounded-lg border border-stone-200" data-id="element-876">
                              <PaperclipIcon className="w-8 h-8 text-stone-400 mx-auto mb-2" data-id="element-877" />
                              <p className="text-sm text-stone-500" data-id="element-878">
                                Ingen bilag tilføjet endnu
                              </p>
                            </div>}
                        </div>
                      </div>
                    </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-879">
                      <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  attachments: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-880">
                        <ChevronDownIcon className="w-4 h-4" data-id="element-881" />
                        Vis Bilag
                        {attachments.length > 0 && <span className="text-stone-500" data-id="element-882">
                            ({attachments.length} bilag)
                          </span>}
                      </button>
                    </motion.div>}
                </AnimatePresence>

                {/* Vigtige bemærkninger */}
                <div data-id="element-883">
                  <RichTextEditor label="Vigtige bemærkninger" value={subTasks} onChange={setSubTasks} placeholder="Vigtige bemærkninger om opgaven" rows={3} data-id="element-884" />
                </div>

                <div className="flex gap-3 pt-2" data-id="element-885">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-886">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-887">
                    Gem ændringer
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}